import { ProfilePermissionType } from '@/api/backend/types';
import { useAuthStore } from '@/store/auth';

/**
 * Custom hook to check if the current user has a specific permission
 * or any of the permissions provided
 * @returns An object with utility functions for permission checks
 */
export const usePermissions = () => {
  const { activeProfile } = useAuthStore();
  /**
   * Check if user has a specific permission
   * @param permission The permission to check
   * @returns true if the user has the permission, false otherwise
   */
  const hasPermission = (permission: ProfilePermissionType): boolean => {
    if (!activeProfile?.permissions) return false;
    return activeProfile.permissions.includes(permission);
  };

  /**
   * Check if user has any of the provided permissions
   * @param permissions Array of permissions to check
   * @returns true if the user has any of the permissions, false otherwise
   */
  const hasAnyPermission = (permissions: ProfilePermissionType[]): boolean => {
    if (!activeProfile?.permissions) return false;
    return permissions.some((permission) => activeProfile.permissions.includes(permission));
  };

  /**
   * Check if user has all of the provided permissions
   * @param permissions Array of permissions to check
   * @returns true if the user has all of the permissions, false otherwise
   */
  const hasAllPermissions = (permissions: ProfilePermissionType[]): boolean => {
    if (!activeProfile?.permissions) return false;
    return permissions.every((permission) => activeProfile.permissions.includes(permission));
  };

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };
};
